import { MediaType } from '../media-types';
import { AttInfoLocalBuffer, AttInfoString } from '../mime-atts/types';
import { LeafMimeNode } from '../mime-tree/types';
import { ReadonlyDeep, StringDecoder } from '../types';
import { cleanPosixBasename, isDef } from '../utils/common';
import { getUrlBasename } from '../utils/ext';
import { decodeTransferEnc } from './decodeTransferEnc';
import { MediaTypeAndCache } from './guessNodeMediaType.types';
import { tryGetBodyStr } from './tryGetBodyStr';

export function nodeToAtt(
	node: ReadonlyDeep<LeafMimeNode>,
	decode: StringDecoder,
	cache1: Readonly<MediaTypeAndCache>
): [AttInfoLocalBuffer | AttInfoString, MediaTypeAndCache] {
	const cache: MediaTypeAndCache = { ...cache1 };

	const mediaType = cache?.mediaType || node.contentType;

	cache.bodyBuf = cache.bodyBuf ?? decodeTransferEnc(node);

	// Try to decode and handle HTML & CSS as string attachments
	if ((mediaType === MediaType.Html || mediaType === MediaType.Css) && !cache.triedGetBodyStr) {
		const bodyStrInfo = tryGetBodyStr(cache.bodyBuf, node.contentTypeCharset, decode);
		cache.triedGetBodyStr = true;
		cache.bodyEnc = bodyStrInfo.bodyEnc;
		cache.bodyStr = bodyStrInfo.bodyStr;
	}

	const filename =
		node.contentDispositionFilename ||
		node.contentTypeName ||
		getUrlBasename(node.contentLocation);
	const generatedFilename = cleanPosixBasename(filename || 'unnamed attachment');

	if (isDef(cache.bodyStr) && (mediaType === MediaType.Html || mediaType === MediaType.Css)) {
		const att: AttInfoString = {
			attType: 'string',
			contentType: mediaType ?? '',
			contentLocation: node.contentLocation,
			contentDisposition: node.contentDisposition,
			fileName: filename,
			contentId: node.contentId ?? '',
			generatedFileName: generatedFilename,
			str: cache.bodyStr,
			toBufEncoding: cache.bodyEnc,
		};
		return [att, cache];
	} else {
		const att: AttInfoLocalBuffer = {
			attType: 'local-buffer',
			contentType: mediaType ?? '',
			contentLocation: node.contentLocation,
			contentDisposition: node.contentDisposition,
			fileName: filename,
			contentId: node.contentId ?? '',
			generatedFileName: generatedFilename,
			length: cache.bodyBuf.length,
			buf: cache.bodyBuf,
			decodeCharset: node.contentTypeCharset,
		};
		return [att, cache];
	}
}
